// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';

/* ═══════════════════════════════════════════════════════════════════════════
 * MON PROFIL — LE MÊME PANNEAU DANS LES TROIS MODULES (RQ, 07/09/2026)
 *
 * « Dans le profil du personnel, peu importe où on est, on retrouve le même
 * menu ; je voudrais un onglet Sécurité pour modifier son mot de passe et
 * voir / retirer les appareils de confiance. L'idée est d'harmoniser à
 * l'identique le profil au travers des différents sites. »
 *
 * ── D'où il vient ──────────────────────────────────────────────────────────
 *
 * Le catalogue avait un panneau complet (mot de passe + appareils) ; Qualité
 * avait un menu « mon espace » (congés, planning, dossier RH) sans rien de
 * tout cela ; le Site n'avait qu'une pastille aux initiales, inerte. Trois
 * profils pour une même personne. Celui-ci reprend la logique du catalogue et
 * la met dans le socle, avec deux onglets :
 *
 *   · « Mon espace »  — les entrées PROPRES AU MODULE, passées par l'appelant
 *                       (Qualité y met congés / planning / dossier RH ; le Site
 *                       et le catalogue n'en ont pas — l'onglet n'apparaît pas) ;
 *   · « Sécurité »    — identique partout : mot de passe, appareils de
 *                       confiance, déconnexion de toutes les sessions.
 *
 * ── Ce qui le rend portable ────────────────────────────────────────────────
 *
 * Il ne parle qu'à `/api/auth/me`, `/api/auth/change-password` et
 * `/api/auth/devices` — trois proxys Next qui existent désormais dans les
 * trois modules (recopiés du catalogue le 07/09) et qui gardent le JWT dans
 * son cookie httpOnly. Aucun magasin de module, aucune fonte d'icônes : les
 * deux glyphes sont dessinés ici. Seuls les jetons présents dans les trois
 * feuilles sont employés.
 *
 * ⚠️ « Déconnecter tous mes appareils » révoque AUSSI la session courante :
 * le proxy efface le cookie, et c'est à l'appelant de ramener l'utilisateur à
 * la connexion — chaque module a la sienne. D'où `apresDeconnexionTotale`.
 * ═══════════════════════════════════════════════════════════════════════════ */

import React from 'react';
import { useSortieFenetre, AlerteSortie } from './fenetreModale';

/** Un appareil reconnu tel que le moteur le rend. Aucun jeton n'en sort. */
type Appareil = { id: string; libelle: string; cree: string; vuLe: string; expire: string };

export type EntreeProfil = { label: string; aide?: string; faire: () => void };

function jour(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const champ: React.CSSProperties = {
  width: '100%', height: 40, border: '1.5px solid var(--border-default)', borderRadius: 'var(--radius-md)',
  padding: '0 12px', fontSize: 14, color: 'var(--text-strong)', font: 'inherit', boxSizing: 'border-box',
};
const etiquette: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 };
const titreH3: React.CSSProperties = { margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' };

const Cadenas = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const Sortie = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export function PanneauProfil({ onFermer, sousTitre, entrees = [], notifier, apresDeconnexionTotale }: {
  onFermer: () => void;
  /** Ce qui s'écrit sous le nom : la fonction, le profil. */
  sousTitre?: string;
  /** Les entrées « Mon espace » propres au module. Vide : l'onglet n'existe pas. */
  entrees?: EntreeProfil[];
  /** Le toast du module. Sans lui, le panneau affiche le message sur place. */
  notifier?: (message: string) => void;
  /** Après « Déconnecter tous mes appareils » : le cookie est déjà effacé,
   *  l'appelant ramène à sa page de connexion. */
  apresDeconnexionTotale: () => void | Promise<void>;
}) {
  const [onglet, setOnglet] = React.useState<'espace' | 'securite'>(entrees.length ? 'espace' : 'securite');
  const [me, setMe] = React.useState<{ name?: string; username?: string; email?: string } | null>(null);
  const [pwd, setPwd] = React.useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = React.useState(false);
  const [appareils, setAppareils] = React.useState<Appareil[] | null>(null);
  const [jours, setJours] = React.useState(30);
  const [devBusy, setDevBusy] = React.useState('');
  const [message, setMessage] = React.useState('');
  const dire = (m: string) => { if (notifier) notifier(m); else setMessage(m); };

  const boite = React.useRef<HTMLDivElement | null>(null);
  const sortie = useSortieFenetre({ boite, onClose: onFermer });

  React.useEffect(() => {
    void fetch('/api/auth/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMe(d?.user || d || null))
      .catch(() => {});
    // Un échec laisse `null` → la section le dit, plutôt qu'une liste vide qui
    // ferait croire qu'aucun appareil n'est reconnu.
    void fetch('/api/auth/devices', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setAppareils(Array.isArray(d.appareils) ? d.appareils : []);
        if (typeof d.jours === 'number') setJours(d.jours);
      })
      .catch(() => {});
  }, []);

  const revoquer = async (a: Appareil) => {
    if (devBusy) return;
    setDevBusy(a.id);
    try {
      const r = await fetch('/api/auth/devices', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ action: 'revoke', id: a.id }),
      });
      if (!r.ok) throw new Error();
      setAppareils((l) => (l || []).filter((x) => x.id !== a.id));
      dire('Appareil retiré : un code sera redemandé à sa prochaine connexion.');
    } catch {
      dire('Impossible de retirer cet appareil.');
    } finally {
      setDevBusy('');
    }
  };

  const deconnecterTout = async () => {
    if (devBusy) return;
    setDevBusy('all');
    try {
      const r = await fetch('/api/auth/devices', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ action: 'logout-all' }),
      });
      if (!r.ok) throw new Error();
      onFermer();
      await apresDeconnexionTotale();
      dire('Toutes vos sessions ont été révoquées. Reconnectez-vous.');
    } catch {
      dire('Échec de la déconnexion des appareils.');
      setDevBusy('');
    }
  };

  const changerMotDePasse = async () => {
    if (!pwd.current || pwd.next.length < 8 || pwd.next !== pwd.confirm) {
      dire(pwd.next !== pwd.confirm ? 'La confirmation ne correspond pas.' : 'Mot de passe : 8 caractères minimum.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch('/api/auth/change-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ currentPassword: pwd.current, password: pwd.next, passwordConfirmation: pwd.confirm }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(r.status < 500 && d?.error?.message ? d.error.message : 'Mot de passe actuel incorrect.');
      setPwd({ current: '', next: '', confirm: '' });
      // La saisie est enregistrée : sans cela, la croix demandait ensuite de
      // « quitter sans enregistrer » un mot de passe déjà changé.
      sortie.saisieEnregistree();
      dire('Mot de passe modifié.');
    } catch (e) {
      dire(e instanceof Error ? e.message : 'Échec de la modification.');
    } finally {
      setBusy(false);
    }
  };

  const initiales = (me?.name || '?').replace(/^(Dr|Pr|Mme|M\.)\s+/i, '').split(/\s+/).map((p) => p[0] || '').slice(0, 2).join('').toUpperCase();
  const ongletStyle = (actif: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: 13, fontWeight: 700, padding: '9px 14px', cursor: 'pointer', background: 'none', border: 'none',
    color: actif ? 'var(--color-primary)' : 'var(--text-muted)', borderBottom: `2px solid ${actif ? 'var(--color-primary)' : 'transparent'}`,
  });

  return (
    /* Le voile ne ferme rien ; la croix et Échap passent par la porte unique
       (règle du 04/09/2026) — ce panneau porte des mots de passe. */
    <div role="dialog" aria-modal="true" aria-label="Mon profil" {...sortie.proprietesVoile}
      style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(11,20,38,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, font: '14px/1.5 var(--font-sans, system-ui)', textTransform: 'none', letterSpacing: 'normal' }}>
      <div ref={boite} {...sortie.proprietesBoite}
        style={{ ...sortie.proprietesBoite.style, position: 'relative', width: '100%', maxWidth: 460, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', background: 'var(--surface-card)', color: 'var(--text-strong)', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 24 }}>
        {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <span style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--text-strong)', color: 'var(--surface-card)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700 }}>{initiales}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{me?.name || '—'}</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{sousTitre || me?.username || ''}</div>
          </div>
          <button type="button" onClick={sortie.demanderSortie} aria-label="Fermer"
            style={{ border: 'none', background: sortie.refuse ? 'var(--color-primary)' : 'var(--surface-sunken)', color: sortie.refuse ? 'var(--surface-card)' : 'var(--text-muted)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, font: 'inherit', transition: 'background 140ms' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gap: 6, fontSize: 13, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><span style={{ color: 'var(--text-muted)' }}>Identifiant</span><span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{me?.username || '—'}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><span style={{ color: 'var(--text-muted)' }}>E-mail</span><span dir="ltr" style={{ fontWeight: 600 }}>{me?.email || '—'}</span></div>
        </div>

        {/* Les onglets — un seul quand le module n'a pas d'entrées propres :
            un onglet unique se dessine quand même, pour que « Sécurité » soit
            au même endroit dans les trois modules. */}
        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-default)', marginBottom: 16 }}>
          {entrees.length > 0 && (
            <button type="button" role="tab" aria-selected={onglet === 'espace'} onClick={() => setOnglet('espace')} style={ongletStyle(onglet === 'espace')}>Mon espace</button>
          )}
          <button type="button" role="tab" aria-selected={onglet === 'securite'} onClick={() => setOnglet('securite')} style={ongletStyle(onglet === 'securite')}>Sécurité</button>
        </div>

        {message && !notifier && (
          <div role="status" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: 'var(--surface-sunken)', fontSize: 12.5, color: 'var(--text-strong)' }}>{message}</div>
        )}

        {onglet === 'espace' && (
          <div style={{ display: 'grid', gap: 6 }}>
            {entrees.map((e) => (
              <button key={e.label} type="button" onClick={() => { onFermer(); e.faire(); }}
                style={{ display: 'block', textAlign: 'start', width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', cursor: 'pointer', font: 'inherit' }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{e.label}</span>
                {e.aide && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>{e.aide}</span>}
              </button>
            ))}
          </div>
        )}

        {onglet === 'securite' && (
          <>
            <h3 style={titreH3}>Changer le mot de passe</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <div><label style={etiquette}>Mot de passe actuel</label><input type="password" autoComplete="current-password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} style={champ} /></div>
              <div><label style={etiquette}>Nouveau mot de passe (8 caractères min.)</label><input type="password" autoComplete="new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} style={champ} /></div>
              <div><label style={etiquette}>Confirmation</label><input type="password" autoComplete="new-password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} style={champ} /></div>
              <button type="button" onClick={() => void changerMotDePasse()} disabled={busy}
                style={{ marginTop: 2, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', font: 'inherit', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Modification…' : 'Modifier le mot de passe'}
              </button>
            </div>

            <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border-default)' }}>
              <h3 style={{ ...titreH3, marginBottom: 4 }}>Appareils de confiance</h3>
              <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                Ces navigateurs ne redemandent pas de code à usage unique pendant {jours} jours.
                Le mot de passe, lui, reste exigé à chaque connexion.
              </p>
              {appareils === null && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Liste indisponible pour le moment.</div>}
              {appareils?.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun appareil reconnu : un code est demandé à chaque connexion.</div>}
              {appareils && appareils.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {appareils.map((a) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)' }}>
                      <span style={{ flex: 'none', color: 'var(--text-muted)', display: 'inline-flex' }}><Cadenas /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.libelle || 'Appareil'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Reconnu le {jour(a.cree)} · vu le {jour(a.vuLe)} · expire le {jour(a.expire)}</div>
                      </div>
                      <button type="button" onClick={() => void revoquer(a)} disabled={devBusy !== ''}
                        style={{ flex: 'none', border: '1px solid var(--border-default)', background: 'var(--surface-card)', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, font: 'inherit', color: 'var(--text-strong)', cursor: devBusy ? 'default' : 'pointer', opacity: devBusy === a.id ? 0.5 : 1 }}>
                        {devBusy === a.id ? 'Retrait…' : 'Retirer'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => void deconnecterTout()} disabled={devBusy !== ''}
                style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', border: '1px solid var(--status-danger)', background: 'var(--surface-card)', color: 'var(--status-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, fontWeight: 600, font: 'inherit', cursor: devBusy ? 'default' : 'pointer', opacity: devBusy === 'all' ? 0.6 : 1 }}>
                <Sortie />{devBusy === 'all' ? 'Déconnexion…' : 'Déconnecter tous mes appareils'}
              </button>
              <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                Révoque toutes les sessions ouvertes, y compris celle-ci, et efface tous les appareils reconnus.
                À utiliser si vous soupçonnez qu’un autre poste est resté connecté.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
